import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_ARK_TASK_IN_FLIGHT_ALLOWANCE,
  type CustomerManagerCustomerSummary,
  type OrgSkuTaskAdmissionOverride,
  type PutRateLimitRequest,
  type QuotaLane,
  type RateLimitSource,
} from '@nadoutong/contracts';
import { getCustomerManagerCustomer360 } from '../../api/customerManagerWorkspace.ts';
import { getCrmUsageDaily } from '../../api/opsUsage.ts';
import {
  listOrgSkuTaskAdmissionOverrides,
  putOrgSkuTaskAdmissionOverride,
  deleteOrgSkuTaskAdmissionOverride,
} from '../../api/taskAdmission.ts';
import {
  QUOTA_REFUSALS_KEY,
  QUOTA_TASK_ADMISSION_KEY,
  useDeleteOrgAllRateLimit,
  useDeleteOrgSkuRateLimit,
  useGlobalRateLimit,
  useOrgAllRateLimits,
  useOrgEffectiveRateLimits,
  useOrgSkuRateLimits,
  usePutOrgAllRateLimit,
  usePutOrgSkuRateLimit,
  useRateLimitRefusals,
  useSkuRateLimits,
} from '../../api/useRoutingLoad.ts';
import { CustomerCompanyLine } from '../../components/identity/CustomerCell.tsx';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Checkbox,
  Chip,
  Drawer,
  Input,
  Panel,
  StatusLamp,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { buildRateLimitWrite, parseRateLimitCap } from '../routing-load/loadSettingsFields.ts';
import { resolveEffective, type LayerCaps } from './resolveEffective.ts';
import { formatCap, SourceChip } from './SourceChip.tsx';
import { TpmField } from './TpmField.tsx';
import { formatTpmCap, formatTpmMInput, parseTpmMPreserving } from './tpmUnits.ts';
import { groupByNamespace, skuShortName } from './skuDisplay.ts';

interface CapsDraft {
  readonly rpm: string;
  readonly tpm: string;
  /** #2738 uncached-input TPM, millions of tokens like `tpm`. */
  readonly tpmUncached: string;
}

type CapDim = 'rpm' | 'tpm' | 'tpm_uncached';

/** Stored token values per layer, so an untouched M-field round-trips exactly. */
interface StoredTokens {
  readonly orgAll: number | null;
  readonly sku: Readonly<Record<string, number | null>>;
}

interface DrawerDraft {
  readonly orgAll: CapsDraft;
  readonly sku: Readonly<Record<string, CapsDraft>>;
  readonly task: Readonly<Record<string, Readonly<Record<string, string>>>>;
}

function emptyCaps(): CapsDraft {
  return { rpm: '', tpm: '', tpmUncached: '' };
}

function capsFromRow(
  rpm: number | null | undefined,
  tpm: number | null | undefined,
  tpmUncached: number | null | undefined,
): CapsDraft {
  return {
    rpm: typeof rpm === 'number' ? String(rpm) : '',
    tpm: formatTpmMInput(tpm),
    tpmUncached: formatTpmMInput(tpmUncached),
  };
}

function parseCapDraft(raw: string, dim: CapDim, original?: number | null) {
  return dim === 'rpm' ? parseRateLimitCap(raw) : parseTpmMPreserving(raw, original);
}

function capValue(raw: string, dim: CapDim, original?: number | null): number | null {
  const parsed = parseCapDraft(raw, dim, original);
  return parsed.kind === 'value' ? parsed.value : null;
}

function layerFromDraft(
  draft: CapsDraft,
  originalTpm?: number | null,
  originalUncached?: number | null,
): LayerCaps | null {
  const rpm = capValue(draft.rpm, 'rpm');
  const tpm = capValue(draft.tpm, 'tpm', originalTpm);
  const tpmUncached = capValue(draft.tpmUncached, 'tpm_uncached', originalUncached);
  if (rpm === null && tpm === null && tpmUncached === null) return null;
  return { rpm, tpm, tpmUncached };
}

function capInvalid(raw: string, dim: CapDim, original?: number | null): boolean {
  return parseCapDraft(raw, dim, original).kind === 'invalid';
}

function hasOverride(caps: CapsDraft | undefined): boolean {
  return caps !== undefined
    && (caps.rpm.trim().length > 0 || caps.tpm.trim().length > 0 || caps.tpmUncached.trim().length > 0);
}

function sameCaps(a: CapsDraft, b: CapsDraft): boolean {
  return a.rpm === b.rpm && a.tpm === b.tpm && a.tpmUncached === b.tpmUncached;
}

function draftsEqual(a: DrawerDraft, b: DrawerDraft): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function peakRpmFromHourly(requestCounts: readonly number[]): number {
  const peakRequests = requestCounts.reduce((max, count) => Math.max(max, count), 0);
  if (peakRequests <= 0) return 0;
  return Math.ceil(peakRequests / 60);
}

export function QuotaCustomerDrawer({
  orgId,
  focusSkuId,
  customers,
  onClose,
}: {
  readonly orgId: string | null;
  readonly focusSkuId: string | null;
  readonly customers: readonly CustomerManagerCustomerSummary[];
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  const open = orgId !== null;
  const cached = customers.find((row) => row.org_id === orgId) ?? null;
  const detail = useQuery({
    queryKey: ['quota-customer-360', orgId ?? ''],
    queryFn: () => getCustomerManagerCustomer360(orgId ?? '', fetchImpl),
    enabled: open && orgId !== null && cached === null,
  });
  const orgName = cached?.org_name ?? detail.data?.customer.org_name ?? orgId ?? '—';
  const orgEmail = cached?.email ?? detail.data?.customer.email ?? '';
  const accountId = cached?.account_id ?? detail.data?.customer.account_id ?? '';

  const effective = useOrgEffectiveRateLimits(orgId ?? '', open);
  const orgAllList = useOrgAllRateLimits();
  const orgSkuList = useOrgSkuRateLimits(orgId);
  const skuDefaults = useSkuRateLimits();
  const globalRow = useGlobalRateLimit();
  const refusalsSince = useMemo(
    () => (orgId === null ? '' : new Date(Date.now() - 3_600_000).toISOString()),
    [orgId],
  );
  const refusals = useRateLimitRefusals(refusalsSince, open);
  const usage = useQuery({
    queryKey: ['quota-peak-usage', accountId],
    queryFn: () => getCrmUsageDaily({ window: '7d', accountId, granularity: 'hour' }, fetchImpl),
    enabled: open && accountId.length > 0,
  });
  const tasks = useQuery({
    queryKey: ['task-admission', orgId ?? ''],
    queryFn: ({ signal }) => listOrgSkuTaskAdmissionOverrides(orgId ?? '', fetchImpl, signal),
    enabled: open && orgId !== null,
  });

  const [draft, setDraft] = useState<DrawerDraft>({ orgAll: emptyCaps(), sku: {}, task: {} });
  const [baseline, setBaseline] = useState<DrawerDraft>({ orgAll: emptyCaps(), sku: {}, task: {} });
  const [storedTpm, setStoredTpm] = useState<StoredTokens>({ orgAll: null, sku: {} });
  const [storedUncached, setStoredUncached] = useState<StoredTokens>({ orgAll: null, sku: {} });
  const [filter, setFilter] = useState('');
  const [onlyOverridden, setOnlyOverridden] = useState(false);
  const [peakMultiplier, setPeakMultiplier] = useState('1.5');
  const [savedFlash, setSavedFlash] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [flashSku, setFlashSku] = useState<string | null>(null);
  const seededFor = useRef<string | null>(null);

  useEffect(() => {
    if (orgId === null) {
      seededFor.current = null;
      setFilter('');
      setOnlyOverridden(false);
      setSavedFlash(false);
      setConfirmClose(false);
      return;
    }
    if (orgSkuList.data === undefined || orgAllList.data === undefined || tasks.data === undefined) return;
    if (seededFor.current === orgId) return;
    const orgAll = orgAllList.data.items.find((row) => row.org_id === orgId);
    const sku: Record<string, CapsDraft> = {};
    const skuTpm: Record<string, number | null> = {};
    const skuUncached: Record<string, number | null> = {};
    for (const row of orgSkuList.data.items) {
      sku[row.sku_id] = capsFromRow(row.rpm, row.tpm, row.tpm_uncached);
      skuTpm[row.sku_id] = row.tpm ?? null;
      skuUncached[row.sku_id] = row.tpm_uncached ?? null;
    }
    const task: Record<string, Record<string, string>> = {};
    for (const item of tasks.data.items) {
      const source = item.overlay_max_in_flight_by_tier_class ?? item.default_max_in_flight_by_tier_class;
      const next: Record<string, string> = {};
      for (const key of Object.keys(item.default_max_in_flight_by_tier_class)) {
        const value = source[key];
        next[key] = typeof value === 'number' ? String(value) : '';
      }
      task[item.sku_id] = next;
    }
    const nextDraft: DrawerDraft = {
      orgAll: capsFromRow(orgAll?.rpm, orgAll?.tpm, orgAll?.tpm_uncached),
      sku,
      task,
    };
    setDraft(nextDraft);
    setBaseline(nextDraft);
    setStoredTpm({ orgAll: orgAll?.tpm ?? null, sku: skuTpm });
    setStoredUncached({ orgAll: orgAll?.tpm_uncached ?? null, sku: skuUncached });
    seededFor.current = orgId;
  }, [orgId, orgAllList.data, orgSkuList.data, tasks.data]);

  useEffect(() => {
    if (focusSkuId === null || orgId === null) return;
    setFlashSku(focusSkuId);
    const timer = window.setTimeout(() => {
      document.querySelector(`[data-sku="${focusSkuId}"]`)?.scrollIntoView({ block: 'center' });
    }, 50);
    const clear = window.setTimeout(() => setFlashSku(null), 1400);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clear);
    };
  }, [focusSkuId, orgId, effective.data]);

  const putOrgAll = usePutOrgAllRateLimit();
  const deleteOrgAll = useDeleteOrgAllRateLimit();
  const putOrgSku = usePutOrgSkuRateLimit();
  const deleteOrgSku = useDeleteOrgSkuRateLimit();
  const putTask = useMutation({
    mutationFn: ({ skuId, overlay }: { skuId: string; overlay: Record<string, number> }) =>
      putOrgSkuTaskAdmissionOverride(orgId ?? '', skuId, { max_in_flight_by_tier_class: overlay }, fetchImpl),
  });
  const deleteTask = useMutation({
    mutationFn: (skuId: string) => deleteOrgSkuTaskAdmissionOverride(orgId ?? '', skuId, fetchImpl),
  });

  const dirty = !draftsEqual(draft, baseline);
  const unlimited = t('quotas.editor.source.none');
  const peakRpm = peakRpmFromHourly((usage.data?.days ?? []).map((day) => day.request_count));
  const refusals1h = (refusals.data?.items ?? []).filter((row) => row.org_id === orgId);
  const refusalsBySku = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of refusals1h) {
      if (row.sku_id === null) continue;
      map.set(row.sku_id, (map.get(row.sku_id) ?? 0) + row.count);
    }
    return map;
  }, [refusals1h]);
  const orgRefusals = refusals1h.reduce((sum, row) => sum + row.count, 0);
  const skuDefaultById = useMemo(() => {
    const map = new Map<string, LayerCaps>();
    for (const item of skuDefaults.data?.items ?? []) {
      map.set(item.sku_id, { rpm: item.rpm, tpm: item.tpm, tpmUncached: item.tpm_uncached ?? null });
    }
    return map;
  }, [skuDefaults.data]);
  const globalCaps: LayerCaps | null = globalRow.data?.item === null || globalRow.data?.item === undefined
    ? null
    : {
      rpm: globalRow.data.item.rpm,
      tpm: globalRow.data.item.tpm,
      tpmUncached: globalRow.data.item.tpm_uncached ?? null,
    };
  const taskBySku = useMemo(() => {
    const map = new Map<string, OrgSkuTaskAdmissionOverride>();
    for (const item of tasks.data?.items ?? []) map.set(item.sku_id, item);
    return map;
  }, [tasks.data]);

  const rows = effective.data?.rows ?? [];
  const filtered = rows.filter((row) => {
    const name = row.sku_name.length > 0 ? row.sku_name : row.sku_id;
    const q = filter.trim().toLowerCase();
    if (q.length > 0 && !name.toLowerCase().includes(q) && !row.sku_id.toLowerCase().includes(q)) {
      return false;
    }
    if (!onlyOverridden) return true;
    return hasOverride(draft.sku[row.sku_id]);
  });
  const groups = groupByNamespace(filtered, (row) => (row.sku_name.length > 0 ? row.sku_name : row.sku_id));

  function requestClose(): void {
    if (dirty) {
      setConfirmClose(true);
      return;
    }
    onClose();
  }

  function discard(): void {
    setDraft(baseline);
    setSavedFlash(false);
    setConfirmClose(false);
  }

  function skuLabel(skuId: string): string {
    const row = rows.find((item) => item.sku_id === skuId);
    const name = row !== undefined && row.sku_name.length > 0 ? row.sku_name : skuId;
    return skuShortName(name);
  }

  async function saveAll(): Promise<void> {
    if (orgId === null) return;
    const orgUncached = { raw: draft.orgAll.tpmUncached, original: storedUncached.orgAll };
    const orgWrite = buildRateLimitWrite(draft.orgAll.rpm, draft.orgAll.tpm, storedTpm.orgAll, orgUncached);
    const baseOrg = buildRateLimitWrite(baseline.orgAll.rpm, baseline.orgAll.tpm, storedTpm.orgAll, {
      raw: baseline.orgAll.tpmUncached,
      original: storedUncached.orgAll,
    });
    if (orgWrite.kind === 'invalid') {
      toast.error(t('quotas.toast.invalid_row', { row: t('quotas.drawer.org_all') }));
      return;
    }
    const skuWrites: { skuId: string; kind: 'delete' | 'put'; body?: PutRateLimitRequest }[] = [];
    const skuIds = new Set([...Object.keys(draft.sku), ...Object.keys(baseline.sku)]);
    for (const skuId of skuIds) {
      const next = draft.sku[skuId] ?? emptyCaps();
      const prev = baseline.sku[skuId] ?? emptyCaps();
      if (sameCaps(next, prev)) continue;
      const write = buildRateLimitWrite(next.rpm, next.tpm, storedTpm.sku[skuId] ?? null, {
        raw: next.tpmUncached,
        original: storedUncached.sku[skuId] ?? null,
      });
      if (write.kind === 'invalid') {
        toast.error(t('quotas.toast.invalid_row', { row: skuLabel(skuId) }));
        return;
      }
      if (write.kind === 'delete') skuWrites.push({ skuId, kind: 'delete' });
      else skuWrites.push({ skuId, kind: 'put', body: write.body });
    }
    const taskWrites: { skuId: string; kind: 'delete' | 'put'; overlay?: Record<string, number> }[] = [];
    const taskIds = new Set([...Object.keys(draft.task), ...Object.keys(baseline.task)]);
    for (const skuId of taskIds) {
      const item = taskBySku.get(skuId);
      if (item === undefined) continue;
      const next = draft.task[skuId] ?? {};
      const prev = baseline.task[skuId] ?? {};
      if (JSON.stringify(next) === JSON.stringify(prev)) continue;
      const overlay: Record<string, number> = {};
      let valid = true;
      for (const key of Object.keys(item.default_max_in_flight_by_tier_class)) {
        const raw = next[key] ?? '';
        const allowance = Number(raw);
        if (!Number.isSafeInteger(allowance) || allowance < 1 || allowance > MAX_ARK_TASK_IN_FLIGHT_ALLOWANCE) {
          valid = false;
          break;
        }
        overlay[key] = allowance;
      }
      if (!valid) {
        toast.error(t('quotas.toast.invalid_row', { row: skuLabel(skuId) }));
        return;
      }
      const hadOverlay = item.overlay_max_in_flight_by_tier_class !== null;
      const matchesDefault = Object.entries(item.default_max_in_flight_by_tier_class)
        .every(([key, value]) => overlay[key] === value);
      if (matchesDefault && hadOverlay) taskWrites.push({ skuId, kind: 'delete' });
      else if (!matchesDefault) taskWrites.push({ skuId, kind: 'put', overlay });
    }
    try {
      if (JSON.stringify(orgWrite) !== JSON.stringify(baseOrg)) {
        if (orgWrite.kind === 'delete') await deleteOrgAll.mutateAsync({ orgId });
        else await putOrgAll.mutateAsync({ orgId, body: orgWrite.body });
      }
      for (const write of skuWrites) {
        if (write.kind === 'delete') await deleteOrgSku.mutateAsync({ orgId, skuId: write.skuId });
        else if (write.body !== undefined) {
          await putOrgSku.mutateAsync({ orgId, skuId: write.skuId, body: write.body });
        }
      }
      for (const write of taskWrites) {
        if (write.kind === 'delete') await deleteTask.mutateAsync(write.skuId);
        else if (write.overlay !== undefined) {
          await putTask.mutateAsync({ skuId: write.skuId, overlay: write.overlay });
        }
      }
      setBaseline(draft);
      const nextSkuTpm: Record<string, number | null> = { ...storedTpm.sku };
      const nextSkuUncached: Record<string, number | null> = { ...storedUncached.sku };
      for (const write of skuWrites) {
        if (write.kind === 'delete') {
          delete nextSkuTpm[write.skuId];
          delete nextSkuUncached[write.skuId];
        } else if (write.body !== undefined) {
          nextSkuTpm[write.skuId] = write.body.tpm ?? null;
          nextSkuUncached[write.skuId] = write.body.tpm_uncached ?? null;
        }
      }
      setStoredTpm({
        orgAll: orgWrite.kind === 'put' ? orgWrite.body.tpm ?? null : orgWrite.kind === 'delete' ? null : storedTpm.orgAll,
        sku: nextSkuTpm,
      });
      setStoredUncached({
        orgAll: orgWrite.kind === 'put'
          ? orgWrite.body.tpm_uncached ?? null
          : orgWrite.kind === 'delete' ? null : storedUncached.orgAll,
        sku: nextSkuUncached,
      });
      setSavedFlash(true);
      void qc.invalidateQueries({ queryKey: ['routing-load-effective'] });
      void qc.invalidateQueries({ queryKey: ['task-admission', orgId] });
      void qc.invalidateQueries({ queryKey: QUOTA_TASK_ADMISSION_KEY });
      void qc.invalidateQueries({ queryKey: QUOTA_REFUSALS_KEY });
      toast.success(t('quotas.toast.saved'));
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  function fillPeak(): void {
    const multiplier = Number(peakMultiplier);
    if (!Number.isFinite(multiplier) || multiplier <= 0) {
      toast.error(t('quotas.toast.invalid'));
      return;
    }
    if (peakRpm <= 0) {
      toast.error(t('quotas.toast.peak_failed'));
      return;
    }
    const rpm = String(Math.max(1, Math.ceil(peakRpm * multiplier)));
    setDraft((prev) => ({ ...prev, orgAll: { ...prev.orgAll, rpm } }));
    setSavedFlash(false);
    toast.success(t('quotas.toast.peak_filled'));
  }

  const orgAllHint = capValue(draft.orgAll.rpm, 'rpm');

  async function clearLeftoverOrgSku(skuId: string, keepRpm?: number | null): Promise<void> {
    if (orgId === null) return;
    try {
      if (typeof keepRpm === 'number') {
        await putOrgSku.mutateAsync({ orgId, skuId, body: { rpm: keepRpm, tpm: null, tpm_uncached: null } });
        setDraft((prev) => {
          const current = prev.sku[skuId] ?? emptyCaps();
          return { ...prev, sku: { ...prev.sku, [skuId]: { rpm: current.rpm, tpm: '', tpmUncached: '' } } };
        });
        setBaseline((prev) => {
          const current = prev.sku[skuId] ?? emptyCaps();
          return { ...prev, sku: { ...prev.sku, [skuId]: { rpm: current.rpm, tpm: '', tpmUncached: '' } } };
        });
        setStoredTpm((prev) => ({ ...prev, sku: { ...prev.sku, [skuId]: null } }));
        setStoredUncached((prev) => ({ ...prev, sku: { ...prev.sku, [skuId]: null } }));
        toast.success(t('quotas.toast.cleared'));
        return;
      }
      await deleteOrgSku.mutateAsync({ orgId, skuId });
      setDraft((prev) => {
        const sku = { ...prev.sku };
        delete sku[skuId];
        return { ...prev, sku };
      });
      setBaseline((prev) => {
        const sku = { ...prev.sku };
        delete sku[skuId];
        return { ...prev, sku };
      });
      setStoredTpm((prev) => {
        const sku = { ...prev.sku };
        delete sku[skuId];
        return { ...prev, sku };
      });
      setStoredUncached((prev) => {
        const sku = { ...prev.sku };
        delete sku[skuId];
        return { ...prev, sku };
      });
      toast.success(t('quotas.toast.cleared'));
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(next) => {
          if (!next) requestClose();
        }}
        width="2xl"
        title={
          <span className="flex min-w-0 flex-col gap-0.5">
            <CustomerCompanyLine orgId={orgId} testId="quota-drawer-company" />
            <span>{orgName}</span>
          </span>
        }
        description={
          <span className="font-mono">
            {orgEmail}
            {orgEmail.length > 0 ? '  ·  ' : ''}
            {orgId ?? ''}
          </span>
        }
        closeLabel={t('common.close')}
        testId="quota-customer-drawer"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          requestClose();
        }}
        footer={
          <div className="flex w-full flex-col gap-2">
            {confirmClose ? (
              <div
                className="flex flex-wrap items-center gap-2 rounded border border-warn-line bg-warn-dim px-3 py-2"
                data-testid="quota-drawer-unsaved"
              >
                <span className="text-dense text-warn">{t('quotas.drawer.unsaved_body')}</span>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setConfirmClose(false);
                    onClose();
                  }}
                  data-testid="quota-drawer-unsaved-confirm"
                >
                  {t('quotas.drawer.unsaved_confirm')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmClose(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            ) : null}
            <div className="flex w-full items-center gap-2">
              <Button
                variant="primary"
                onClick={() => { void saveAll(); }}
                data-testid="quota-drawer-save"
              >
                {t('quotas.drawer.save')}
              </Button>
              <Button variant="ghost" onClick={discard} data-testid="quota-drawer-discard">
                {t('quotas.drawer.discard')}
              </Button>
              <span className="ml-auto text-dense text-paper-low" data-testid="quota-drawer-dirty">
                {savedFlash && !dirty ? t('quotas.drawer.saved') : dirty ? t('quotas.drawer.dirty') : t('quotas.drawer.clean')}
              </span>
            </div>
          </div>
        }
      >
        <div className="mb-4 flex flex-wrap gap-8" data-testid="quota-drawer-stats">
          <div>
            <p className="m-0 font-mono text-stat text-paper" data-testid="quota-drawer-stat-refusals">{orgRefusals}</p>
            <p className="mb-0 mt-0.5 text-micro text-paper-low">{t('quotas.drawer.refusals_stat')}</p>
          </div>
          <div>
            <p className="m-0 font-mono text-stat text-paper" data-testid="quota-drawer-stat-peak">
              {peakRpm > 0 ? peakRpm.toLocaleString() : '—'}
            </p>
            <p className="mb-0 mt-0.5 text-micro text-paper-low">{t('quotas.drawer.peak_stat')}</p>
          </div>
          <div>
            <p className="m-0 font-mono text-stat text-paper" data-testid="quota-drawer-stat-skus">{rows.length}</p>
            <p className="mb-0 mt-0.5 text-micro text-paper-low">{t('quotas.drawer.sku_stat')}</p>
          </div>
        </div>

        <Panel caption={t('quotas.drawer.org_all')} padded className="mb-4">
          <p className="m-0 mb-3 text-dense text-paper-low">{t('quotas.drawer.org_all_hint')}</p>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-dense text-paper-mid">{t('quotas.editor.rpm')}</span>
            <Input
              mono
              wrapperClassName="w-24"
              value={draft.orgAll.rpm}
              placeholder={t('quotas.editor.inherit')}
              invalid={capInvalid(draft.orgAll.rpm, 'rpm')}
              className={draft.orgAll.rpm.length === 0 ? 'italic' : undefined}
              onChange={(event) => {
                const rpm = event.target.value;
                setDraft((prev) => ({ ...prev, orgAll: { ...prev.orgAll, rpm } }));
                setSavedFlash(false);
              }}
              data-testid="quota-drawer-org-all-rpm"
            />
            <span className="text-dense text-paper-mid">{t('quotas.editor.tpm')}</span>
            <TpmField
              wrapperClassName="w-28"
              value={draft.orgAll.tpm}
              placeholder={t('quotas.editor.inherit')}
              invalid={capInvalid(draft.orgAll.tpm, 'tpm', storedTpm.orgAll)}
              className={draft.orgAll.tpm.length === 0 ? 'italic' : undefined}
              onChange={(event) => {
                const tpm = event.target.value;
                setDraft((prev) => ({ ...prev, orgAll: { ...prev.orgAll, tpm } }));
                setSavedFlash(false);
              }}
              data-testid="quota-drawer-org-all-tpm"
            />
            <span className="text-dense text-paper-mid">{t('quotas.editor.tpm_uncached')}</span>
            <TpmField
              wrapperClassName="w-28"
              value={draft.orgAll.tpmUncached}
              placeholder={t('quotas.editor.inherit')}
              invalid={capInvalid(draft.orgAll.tpmUncached, 'tpm_uncached', storedUncached.orgAll)}
              className={draft.orgAll.tpmUncached.length === 0 ? 'italic' : undefined}
              onChange={(event) => {
                const tpmUncached = event.target.value;
                setDraft((prev) => ({ ...prev, orgAll: { ...prev.orgAll, tpmUncached } }));
                setSavedFlash(false);
              }}
              data-testid="quota-drawer-org-all-tpm-uncached"
            />
            <Input
              mono
              wrapperClassName="w-16"
              value={peakMultiplier}
              onChange={(event) => setPeakMultiplier(event.target.value)}
              data-testid="quota-drawer-peak-multiplier"
            />
            <Button onClick={fillPeak} data-testid="quota-drawer-fill-peak">
              {t('quotas.editor.fill_peak', { multiplier: peakMultiplier })}
            </Button>
            {orgAllHint !== null ? (
              <span className="text-dense text-paper-low">{t('quotas.drawer.org_all_preview', { rpm: String(orgAllHint) })}</span>
            ) : null}
          </div>
        </Panel>

        <div className="mb-3 flex flex-wrap items-center gap-3">
          <Input
            wrapperClassName="w-64"
            value={filter}
            placeholder={t('quotas.drawer.filter')}
            onChange={(event) => setFilter(event.target.value)}
            data-testid="quota-drawer-filter"
          />
          <Checkbox
            checked={onlyOverridden}
            onCheckedChange={setOnlyOverridden}
            label={t('quotas.drawer.only_overridden')}
            testId="quota-drawer-only-overridden"
          />
          <span className="ml-auto text-dense text-paper-low">{t('quotas.drawer.rule')}</span>
        </div>

        <Table testId="quota-drawer-sku-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('quotas.drawer.sku')}</Th>
              <Th num>{t('quotas.drawer.effective_rpm')}</Th>
              <Th>{t('quotas.drawer.source')}</Th>
              <Th num>{t('quotas.drawer.effective_tpm')}</Th>
              <Th>{t('quotas.drawer.source')}</Th>
              <Th num>{t('quotas.drawer.effective_tpm_uncached')}</Th>
              <Th>{t('quotas.drawer.source')}</Th>
              <Th num>{t('quotas.drawer.org_sku_rpm')}</Th>
              <Th num>{t('quotas.drawer.org_sku_tpm')}</Th>
              <Th num>{t('quotas.drawer.org_sku_tpm_uncached')}</Th>
              <Th>{t('quotas.drawer.task')}</Th>
              <Th>{t('quotas.drawer.refusals_1h')}</Th>
            </Tr>
          </THead>
          <TBody>
            {groups.map((group) => {
              const overrideCount = group.items.filter((row) => hasOverride(draft.sku[row.sku_id])).length;
              return (
                <DrawerGroup
                  key={group.namespace}
                  namespace={group.namespace}
                  count={group.items.length}
                  overrides={overrideCount}
                  items={group.items}
                  draft={draft}
                  flashSku={flashSku}
                  unlimited={unlimited}
                  orgAll={layerFromDraft(draft.orgAll, storedTpm.orgAll, storedUncached.orgAll)}
                  skuDefaultById={skuDefaultById}
                  globalCaps={globalCaps}
                  taskBySku={taskBySku}
                  refusalsBySku={refusalsBySku}
                  storedSkuTpm={storedTpm.sku}
                  storedSkuUncached={storedUncached.sku}
                  onClearLeftover={(skuId, keepRpm) => { void clearLeftoverOrgSku(skuId, keepRpm); }}
                  onSkuCap={(skuId, dim, value) => {
                    setDraft((prev) => {
                      const current = prev.sku[skuId] ?? emptyCaps();
                      const nextCaps: CapsDraft = dim === 'rpm'
                        ? { ...current, rpm: value }
                        : dim === 'tpm' ? { ...current, tpm: value } : { ...current, tpmUncached: value };
                      const sku = { ...prev.sku };
                      if (!hasOverride(nextCaps)) {
                        delete sku[skuId];
                      } else {
                        sku[skuId] = nextCaps;
                      }
                      return { ...prev, sku };
                    });
                    setSavedFlash(false);
                  }}
                  onTaskCap={(skuId, key, value) => {
                    setDraft((prev) => ({
                      ...prev,
                      task: {
                        ...prev.task,
                        [skuId]: { ...(prev.task[skuId] ?? {}), [key]: value },
                      },
                    }));
                    setSavedFlash(false);
                  }}
                />
              );
            })}
          </TBody>
        </Table>
      </Drawer>
    </>
  );
}

function DrawerGroup({
  namespace,
  count,
  overrides,
  items,
  draft,
  flashSku,
  unlimited,
  orgAll,
  skuDefaultById,
  globalCaps,
  taskBySku,
  refusalsBySku,
  storedSkuTpm,
  storedSkuUncached,
  onSkuCap,
  onTaskCap,
  onClearLeftover,
}: {
  readonly namespace: string;
  readonly count: number;
  readonly overrides: number;
  readonly items: readonly {
    sku_id: string;
    sku_name: string;
    rpm: number | null;
    rpm_source: RateLimitSource;
    tpm: number | null;
    tpm_source: RateLimitSource;
    task_lane?: boolean;
    quota_lane?: QuotaLane;
  }[];
  readonly draft: DrawerDraft;
  readonly flashSku: string | null;
  readonly unlimited: string;
  readonly orgAll: LayerCaps | null;
  readonly skuDefaultById: ReadonlyMap<string, LayerCaps>;
  readonly globalCaps: LayerCaps | null;
  readonly taskBySku: ReadonlyMap<string, OrgSkuTaskAdmissionOverride>;
  readonly refusalsBySku: ReadonlyMap<string, number>;
  readonly storedSkuTpm: Readonly<Record<string, number | null>>;
  readonly storedSkuUncached: Readonly<Record<string, number | null>>;
  readonly onSkuCap: (skuId: string, dim: CapDim, value: string) => void;
  readonly onTaskCap: (skuId: string, key: string, value: string) => void;
  readonly onClearLeftover: (skuId: string, keepRpm?: number | null) => void;
}): JSX.Element {
  const t = useT();
  return (
    <>
      <Tr interactive={false} data-testid={`quota-drawer-group-${namespace}`}>
        <Td colSpan={12} className="bg-canvas text-label tracking-normal text-paper-low">
          {namespace}
          {' · '}
          {t('quotas.editor.group_meta', { count: String(count), overrides: String(overrides) })}
        </Td>
      </Tr>
      {items.map((row) => {
        const name = row.sku_name.length > 0 ? row.sku_name : row.sku_id;
        const os = draft.sku[row.sku_id] ?? emptyCaps();
        const originalTpm = storedSkuTpm[row.sku_id] ?? null;
        const originalUncached = storedSkuUncached[row.sku_id] ?? null;
        const resolved = resolveEffective({
          orgSku: layerFromDraft(os, originalTpm, originalUncached),
          orgAll,
          sku: skuDefaultById.get(row.sku_id) ?? null,
          global: globalCaps,
        });
        const task = taskBySku.get(row.sku_id);
        const rejected = refusalsBySku.get(row.sku_id) ?? 0;
        return (
          <Tr
            key={row.sku_id}
            interactive={false}
            data-sku={row.sku_id}
            data-testid={`quota-drawer-sku-${row.sku_id}`}
            className={flashSku === row.sku_id ? 'bg-signal-dim' : undefined}
          >
            <Td mono>
              <span title={name}>{skuShortName(name)}</span>
            </Td>
            {row.task_lane === true || row.quota_lane === 'task' ? (
              <Td colSpan={9} className="text-paper-low" data-testid={`quota-drawer-task-lane-${row.sku_id}`}>
                <span>{t('quotas.drawer.task_lane')}</span>
                {hasOverride(os) ? (
                  <>
                    <Chip tone="warn" lamp={false} className="ml-2" testId={`quota-drawer-task-lane-warn-${row.sku_id}`}>
                      {t('quotas.task_lane.ignores_rpm')}
                    </Chip>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-2"
                      onClick={() => onClearLeftover(row.sku_id)}
                      data-testid={`quota-drawer-clear-leftover-${row.sku_id}`}
                    >
                      {t('quotas.task_lane.clear_leftover')}
                    </Button>
                  </>
                ) : null}
              </Td>
            ) : (
              <>
                <Td num data-testid={`quota-drawer-effective-rpm-${row.sku_id}`}>
                  {formatCap(resolved.rpm.value, unlimited)}
                </Td>
                <Td><SourceChip source={resolved.rpm.source} /></Td>
                {row.quota_lane === 'image' ? (
                  <Td colSpan={4} className="text-paper-low" data-testid={`quota-drawer-image-lane-${row.sku_id}`}>
                    <span>{t('quotas.image_lane.rpm_only')}</span>
                    {typeof originalTpm === 'number' || typeof originalUncached === 'number' ? (
                      <>
                        <Chip tone="warn" lamp={false} className="ml-2" testId={`quota-drawer-image-lane-warn-${row.sku_id}`}>
                          {t('quotas.image_lane.ignores_tpm')}
                        </Chip>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-2"
                          onClick={() => onClearLeftover(row.sku_id, capValue(os.rpm, 'rpm'))}
                          data-testid={`quota-drawer-clear-leftover-${row.sku_id}`}
                        >
                          {t('quotas.task_lane.clear_leftover')}
                        </Button>
                      </>
                    ) : null}
                  </Td>
                ) : (
                  <>
                    <Td num data-testid={`quota-drawer-effective-tpm-${row.sku_id}`}>
                      {formatTpmCap(resolved.tpm.value, unlimited)}
                    </Td>
                    <Td><SourceChip source={resolved.tpm.source} /></Td>
                    <Td num data-testid={`quota-drawer-effective-tpm-uncached-${row.sku_id}`}>
                      {formatTpmCap(resolved.tpmUncached.value, unlimited)}
                    </Td>
                    <Td>
                      <SourceChip
                        source={resolved.tpmUncached.source}
                        testId={`quota-drawer-tpm-uncached-source-${row.sku_id}`}
                      />
                    </Td>
                  </>
                )}
                <Td num>
                  <Input
                    mono
                    wrapperClassName="w-24"
                    value={os.rpm}
                    placeholder={t('quotas.editor.inherit')}
                    invalid={capInvalid(os.rpm, 'rpm')}
                    className={os.rpm.length === 0 ? 'italic' : undefined}
                    onChange={(event) => onSkuCap(row.sku_id, 'rpm', event.target.value)}
                    data-testid={`quota-drawer-org-sku-rpm-${row.sku_id}`}
                  />
                </Td>
                {row.quota_lane === 'image' ? (
                  <Td colSpan={2} />
                ) : (
                  <>
                    <Td num>
                      <TpmField
                        wrapperClassName="w-24"
                        value={os.tpm}
                        placeholder={t('quotas.editor.inherit')}
                        invalid={capInvalid(os.tpm, 'tpm', originalTpm)}
                        className={os.tpm.length === 0 ? 'italic' : undefined}
                        onChange={(event) => onSkuCap(row.sku_id, 'tpm', event.target.value)}
                        data-testid={`quota-drawer-org-sku-tpm-${row.sku_id}`}
                      />
                    </Td>
                    <Td num>
                      <TpmField
                        wrapperClassName="w-24"
                        value={os.tpmUncached}
                        placeholder={t('quotas.editor.inherit')}
                        invalid={capInvalid(os.tpmUncached, 'tpm_uncached', originalUncached)}
                        className={os.tpmUncached.length === 0 ? 'italic' : undefined}
                        onChange={(event) => onSkuCap(row.sku_id, 'tpm_uncached', event.target.value)}
                        data-testid={`quota-drawer-org-sku-tpm-uncached-${row.sku_id}`}
                      />
                    </Td>
                  </>
                )}
              </>
            )}
            <Td>
              {task === undefined ? (
                <span className="text-paper-low">—</span>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Object.keys(task.default_max_in_flight_by_tier_class).map((key) => (
                    <label key={key} className="flex items-center gap-1 text-micro text-paper-low">
                      <span className="font-mono">{key}</span>
                      <Input
                        mono
                        type="number"
                        min={1}
                        max={MAX_ARK_TASK_IN_FLIGHT_ALLOWANCE}
                        wrapperClassName="w-16"
                        value={draft.task[row.sku_id]?.[key] ?? ''}
                        onChange={(event) => onTaskCap(row.sku_id, key, event.target.value)}
                        data-testid={`quota-drawer-task-${row.sku_id}-${key}`}
                      />
                    </label>
                  ))}
                </div>
              )}
            </Td>
            <Td>
              {rejected > 0 ? (
                <Chip tone="bad">{t('quotas.drawer.rejected', { count: String(rejected) })}</Chip>
              ) : (
                <StatusLamp tone="ok" size="sm" />
              )}
            </Td>
          </Tr>
        );
      })}
    </>
  );
}
